// src/components/forms/shared/modal/PetGroomingManager.tsx

import React, { useState } from 'react';
import { Box, styled, Typography } from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { StyledTextField } from '../FormElements';
import { ModalCancelButton, ModalSaveButton } from '../../../common/Buttons';
import {
  SectionContainer,
  SectionDivider,
  InputContainer,
  IncrementButton,
  ManagerFooter,
} from '../../../common/modal/ModalElements';

interface PetGroomingManagerProps {
  petId: number;
  groomSmooth: number;
  groomLong: number;
  groomCut: number;
  onSave: (smooth: number, long: number, cut: number) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: '1.9rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.3),
}));

const GroomingTextField = styled(StyledTextField)({
  flex: 1,
  maxWidth: '140px',
  '& input': {
    textAlign: 'center',
    fontSize: '1.8rem',
    fontWeight: 600,
  },
});

const PetGroomingManager: React.FC<PetGroomingManagerProps> = ({
  petId,
  groomSmooth,
  groomLong,
  groomCut,
  onSave,
  onCancel,
  isLoading = false,
}) => {
  const [smooth, setSmooth] = useState<number>(groomSmooth);
  const [long, setLong] = useState<number>(groomLong);
  const [cut, setCut] = useState<number>(groomCut);

  const validateCount = (value: number): number => {
    let validated = value;
    if (validated < 0) validated = 0;
    return Math.floor(validated);
  };

  const handleSmoothInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) setSmooth(validateCount(value));
  };

  const handleLongInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) setLong(validateCount(value));
  };

  const handleCutInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) setCut(validateCount(value));
  };

  const canDecrementSmooth = smooth > 0;
  const canDecrementLong = long > 0;
  const canDecrementCut = cut > 0;

  return (
    <Box>
      <SectionContainer sx={{ mb: 2 }}>
        <SectionLabel>Groom Smooth</SectionLabel>
        <InputContainer>
          <IncrementButton
            onClick={() => setSmooth((prev) => validateCount(prev - 1))}
            disabled={!canDecrementSmooth}
            title="Decrease smooth grooms"
          >
            <RemoveIcon />
          </IncrementButton>

          <GroomingTextField
            type="number"
            value={smooth}
            onChange={handleSmoothInputChange}
            inputProps={{ min: 0, step: 1 }}
            size="small"
          />

          <IncrementButton
            onClick={() => setSmooth((prev) => validateCount(prev + 1))}
            title="Increase smooth grooms"
          >
            <AddIcon />
          </IncrementButton>
        </InputContainer>
      </SectionContainer>

      <SectionDivider sx={{ my: 1 }} />

      <SectionContainer sx={{ mb: 2 }}>
        <SectionLabel>Groom Long</SectionLabel>
        <InputContainer>
          <IncrementButton
            onClick={() => setLong((prev) => validateCount(prev - 1))}
            disabled={!canDecrementLong}
            title="Decrease long grooms"
          >
            <RemoveIcon />
          </IncrementButton>

          <GroomingTextField
            type="number"
            value={long}
            onChange={handleLongInputChange}
            inputProps={{ min: 0, step: 1 }}
            size="small"
          />

          <IncrementButton
            onClick={() => setLong((prev) => validateCount(prev + 1))}
            title="Increase long grooms"
          >
            <AddIcon />
          </IncrementButton>
        </InputContainer>
      </SectionContainer>

      <SectionDivider sx={{ my: 1 }} />

      <SectionContainer sx={{ mb: 2 }}>
        <SectionLabel>Cut</SectionLabel>
        <InputContainer>
          <IncrementButton
            onClick={() => setCut((prev) => validateCount(prev - 1))}
            disabled={!canDecrementCut}
            title="Decrease cuts"
          >
            <RemoveIcon />
          </IncrementButton>

          <GroomingTextField
            type="number"
            value={cut}
            onChange={handleCutInputChange}
            inputProps={{ min: 0, step: 1 }}
            size="small"
          />

          <IncrementButton
            onClick={() => setCut((prev) => validateCount(prev + 1))}
            title="Increase cuts"
          >
            <AddIcon />
          </IncrementButton>
        </InputContainer>
      </SectionContainer>

      <ManagerFooter>
        <ModalCancelButton onClick={onCancel} disabled={isLoading} />
        <ModalSaveButton
          onClick={() => onSave(smooth, long, cut)}
          disabled={isLoading}
        />
      </ManagerFooter>
    </Box>
  );
};

export default PetGroomingManager;
